import { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { msg, t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Copy, Factory, PaperPlaneTilt } from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';
import { CircularProgress, useTheme } from '@mui/material';
import { apiSuppliers } from 'api/suppliers';
import { Avatar } from 'components/avatar/Avatar';

import type { ComboBoxOption } from 'components/inputs/types';

import { useUser } from 'features/auth/hooks/useUser';
import { useAssociatedBrands } from 'features/suppliers/hooks/useAssociatedBrands';
import { useNotification } from 'modules/notifications/hooks';
import { Modal } from 'modules/modalManager/components/Modal';
import { usePost } from 'lib/api';
import { ReactQueryLoader } from 'lib/react-query';
import { styledTheme } from 'styles/theme/theme';
import { Text } from 'components/text/Text';
import { Chip } from 'components/chip/Chip';
import { Autocomplete } from 'components/inputs/Autocomplete';
import { IconButton } from 'components/button/IconButton';
import { Tooltip } from 'components/tooltip/Tooltip';

type SendInviteCodeRequest = {
  designerOrganizationIds: number[];
};

type GuidedOnboardingWizardProps = {
  open: boolean;
  onClose: (dontShowAgain?: boolean) => void;
  /**
   * `full` — post-registration wizard on the supplier page.
   * `inviteOnly` — header invite icon opens the send-invite / brand-search step only.
   */
  mode?: 'full' | 'inviteOnly';
};

type BrandOptionProps = {
  label: string;
  color?: string;
};

const STEPS = {
  welcome: 0,
  account: 1,
  inviteCode: 2,
  subsuppliers: 3,
} as const;

/** Shared spacing for every wizard step: content 16 top / 24 bottom, footer 24 all sides. */
const wizardModalSx = {
  window: { minWidth: '560px', maxWidth: '560px' },
  windowBody: {
    padding: `${styledTheme.s.md} ${styledTheme.s.lg} ${styledTheme.s.lg}`,
  },
  windowActions: { padding: styledTheme.s.lg },
} as const;

const centeredWizardModalSx = {
  ...wizardModalSx,
  windowBody: { ...wizardModalSx.windowBody, textAlign: 'center' },
} as const;

const inviteCodeModalSx = {
  ...wizardModalSx,
  windowBody: {
    ...wizardModalSx.windowBody,
    marginTop: styledTheme.s.lg,
    borderTop: `1px solid ${styledTheme.palette.border.primarySubtle}`,
    '&&': { paddingTop: styledTheme.s.lg },
  },
  windowActions: {
    ...wizardModalSx.windowActions,
    borderTop: `1px solid ${styledTheme.palette.border.primarySubtle}`,
  },
} as const;

const subsuppliersModalSx = {
  ...wizardModalSx,
  windowBody: { display: 'none' },
  windowActions: {
    ...wizardModalSx.windowActions,
    borderTop: `1px solid ${styledTheme.palette.border.primarySubtle}`,
  },
} as const;

// Module-scope constant — `msg` (not `t`) so the label resolves per-locale at render
// time via useLingui()._() below, instead of once at import time.
const cancelActionMsg = msg`Cancel`;
const nextAction = (onClick: () => void) => ({
  id: 'apply' as const,
  label: t`Next`,
  onClick,
});

const BrandsLoading = () => (
  <BrandsStatus data-testid="guided-onboarding-brands-loading">
    <CircularProgress size={20} aria-label={t`Loading brands`} />
  </BrandsStatus>
);

type NoBrandsFallbackProps = {
  inviteCode?: string;
  onCopy: () => void;
  'data-testid': string;
};

/**
 * Replaces the whole brand-picker section — description text included — when the brands
 * lookup fails, or succeeds with no associated brands to pick from.
 */
const NoBrandsFallback = ({
  inviteCode,
  onCopy,
  'data-testid': dataTestId,
}: NoBrandsFallbackProps) => (
  <NoBrandsMessage data-testid={dataTestId}>
    <Text variant="bodySm" color="secondary">
      <Trans>
        No associated brands found. Copy the invite code share with your directly with the brands
        you are working with
      </Trans>
    </Text>
    {inviteCode && (
      <InviteCodeRow>
        <InviteCodeText>{inviteCode}</InviteCodeText>
        <Tooltip title={t`Copy invite code`}>
          <div>
            <IconButton data-testid="guided-onboarding-copy-invite-code" size="lg" onClick={onCopy}>
              <Copy size={18} />
            </IconButton>
          </div>
        </Tooltip>
      </InviteCodeRow>
    )}
  </NoBrandsMessage>
);

const renderSelectedBrands = (
  value: ComboBoxOption[],
  getItemProps: (args: { index: number }) => { key: number } & Record<string, unknown>
) =>
  value.map((option, index) => {
    const { key, ...itemProps } = getItemProps({ index });
    return (
      <Chip
        key={key}
        label={option.label}
        size="sm"
        isBordered
        isTooltip={false}
        avatar={<Avatar size="xs" letters={option.label} color={option.avatarColor ?? undefined} />}
        {...itemProps}
      />
    );
  });

export const GuidedOnboardingWizard = ({
  open,
  onClose,
  mode = 'full',
}: GuidedOnboardingWizardProps) => {
  const theme = useTheme();
  const { _ } = useLingui();
  const { notify } = useNotification();
  const { data: user } = useUser();
  const inviteCode = user?.loginContext?.inviteCode;
  const cancelAction = { id: 'cancel', label: _(cancelActionMsg) };
  const isInviteOnly = mode === 'inviteOnly';
  const [step, setStep] = useState<number>(isInviteOnly ? STEPS.inviteCode : STEPS.welcome);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState<ComboBoxOption[]>([]);

  const {
    brands: associatedBrands,
    error: associatedBrandsError,
    status: associatedBrandsStatus,
  } = useAssociatedBrands(open && (isInviteOnly || step === STEPS.inviteCode));

  const { mutate: sendInviteCode, isPending: isSending } = usePost<
    SendInviteCodeRequest & { id: string | number }
  >(apiSuppliers.post.sendInviteCode);

  useEffect(() => {
    if (!open) {
      setStep(isInviteOnly ? STEPS.inviteCode : STEPS.welcome);
      setDontShowAgain(false);
      setSelectedBrands([]);
      return;
    }
    if (isInviteOnly) setStep(STEPS.inviteCode);
  }, [open, isInviteOnly]);

  const brandOptions = useMemo<ComboBoxOption[]>(
    () =>
      associatedBrands.map(brand => ({
        id: brand.id,
        label: brand.name,
        avatarColor: brand.avatarColor,
        CustomElement: BrandOption,
        customProps: { color: brand.avatarColor },
      })),
    [associatedBrands]
  );

  const hasNoBrands = associatedBrandsStatus === 'success' && brandOptions.length === 0;

  if (!open) return null;

  const close = () => onClose(dontShowAgain);

  const handleCopyInviteCode = async () => {
    if (inviteCode && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(inviteCode);
      notify.info({
        id: 'guided-onboarding-invite-code-copy',
        message: t`Copied to clipboard!`,
        noDuplicate: true,
      });
    }
  };

  const next = () => {
    if (step === STEPS.subsuppliers) {
      onClose(true);
      return;
    }
    setStep(currentStep => currentStep + 1);
  };

  const send = () => {
    sendInviteCode(
      {
        data: {
          designerOrganizationIds: selectedBrands.map(brand => Number(brand.id)),
        },
      },
      {
        onSuccess: () => {
          notify.success({
            id: 'guided-onboarding-invite-code-sent',
            header: t`Invite code shared`,
            message: t`Your brands have been notified about your new portal account`,
          });
          if (isInviteOnly) {
            close();
            return;
          }
          next();
        },
        onError: error => {
          // Error toast comes from usePost → adapter (common approach for all mutation errors).
          // Do not add a second notify.error here — it would stack with the envelope toast.
          Sentry.captureException(error, {
            tags: { feature: 'guided-onboarding', action: 'send-invite-code' },
          });
        },
      }
    );
  };

  const iconColor = theme.palette.fg.secondary;
  const factoryIcon = <Factory size={48} color={iconColor} />;
  const nextActions = [cancelAction, nextAction(next)];
  const startActions = [{ id: 'apply', label: t`Start`, onClick: next }];

  if (step === STEPS.welcome && !isInviteOnly) {
    return (
      <Modal
        open
        id="guided-onboarding-welcome"
        data-testid="guided-onboarding-welcome"
        variant="centerAligned"
        icon={factoryIcon}
        title={t`Welcome to the Supplier Portal!`}
        description={t`We'll walk you through some steps to get started.`}
        descriptionProps={{ align: 'center' }}
        modalSx={centeredWizardModalSx}
        dontShowAgain={dontShowAgain}
        onDontShowAgainChange={(_event, checked) => setDontShowAgain(checked)}
        actions={nextActions}
        onClose={close}
      />
    );
  }

  if (step === STEPS.account && !isInviteOnly) {
    return (
      <Modal
        open
        id="guided-onboarding-account"
        data-testid="guided-onboarding-account"
        variant="centerAligned"
        hideDontShowAgain
        icon={factoryIcon}
        title={t`One account, for all your brand relationships`}
        description={t`This account will now be the central hub for your brand relationships. This means you will need to merge all your legacy Delogue accounts with this account. We will walk you through the process on the next step.`}
        descriptionProps={{ align: 'center' }}
        modalSx={centeredWizardModalSx}
        actions={nextActions}
        onClose={close}
      />
    );
  }

  if (step === STEPS.inviteCode || isInviteOnly) {
    return (
      <Modal
        open
        id="guided-onboarding-invite-code"
        data-testid="guided-onboarding-invite-code"
        hideDontShowAgain
        variant="horizontalAligned"
        icon={<PaperPlaneTilt size={48} color={iconColor} />}
        title={t`Send invite code`}
        description={t`Share your invite code with all the designers you work with in Delogue so they can merge your legacy accounts with your new portal account.`}
        titleProps={{ variant: 'headlineSm' }}
        descriptionProps={{ variant: 'bodySm' }}
        modalSx={inviteCodeModalSx}
        actions={[
          cancelAction,
          {
            id: 'apply',
            label: t`Send`,
            // Covers loading, error, and no-brands states too — none of them can
            // populate a selection, so `selectedBrands` stays empty.
            disabled: selectedBrands.length === 0 || isSending,
            onClick: send,
          },
        ]}
        onClose={close}
      >
        <InviteCodeContent>
          <ReactQueryLoader
            status={associatedBrandsStatus === 'error' ? 'success' : associatedBrandsStatus}
            error={associatedBrandsError}
            LoadingComponent={BrandsLoading}
          >
            {associatedBrandsStatus === 'error' || hasNoBrands ? (
              <NoBrandsFallback
                data-testid={
                  associatedBrandsStatus === 'error'
                    ? 'guided-onboarding-brands-error'
                    : 'guided-onboarding-brands-empty'
                }
                inviteCode={inviteCode}
                onCopy={handleCopyInviteCode}
              />
            ) : (
              <>
                <Text variant="bodySm" color="placeholder" weight="semiBold">
                  <Trans>
                    Below are all the brands associated with this email address. Select the brands
                    you would like to share your invite code with.
                  </Trans>
                </Text>
                <Autocomplete
                  multiple
                  options={brandOptions}
                  value={selectedBrands}
                  onChange={(_event, value) => setSelectedBrands(value ?? [])}
                  getOptionLabel={option => option.label}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  variant="outlined"
                  placeholder={t`Search brands`}
                  data-testid="guided-onboarding-brand-select"
                  renderValue={renderSelectedBrands}
                />
              </>
            )}
          </ReactQueryLoader>
        </InviteCodeContent>
      </Modal>
    );
  }

  return (
    <Modal
      open
      id="guided-onboarding-subsuppliers"
      data-testid="guided-onboarding-subsuppliers"
      hideDontShowAgain
      hideCloseIcon
      variant="horizontalAligned"
      icon={factoryIcon}
      title={t`Start adding your subsuppliers and facilities`}
      description={t`Building your subsupplier library allows the brands you work with to have full transparency about your supply chain and allows you to use this information to easily complete supply chain mapping requests.`}
      contents={[]}
      headerProps={{ sx: { '&&': { paddingBottom: styledTheme.s.lg } } }}
      titleProps={{ variant: 'headlineSm' }}
      descriptionProps={{ variant: 'bodySm' }}
      modalSx={subsuppliersModalSx}
      actions={startActions}
      onClose={close}
    />
  );
};

const BrandOption = ({ label, color }: BrandOptionProps) => (
  <BrandOptionContainer>
    <Avatar size="xsl" letters={label} color={color} />
    <Text variant="bodySm" color="secondary">
      {label}
    </Text>
  </BrandOptionContainer>
);

const InviteCodeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.s.xxs};
  width: 100%;
`;

/** Occupies the brand picker's slot while the query is pending or has failed. */
const BrandsStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
`;

/** Replaces the brand-picker section when there are no brands to share the invite code with. */
const NoBrandsMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.s.sm};
  width: 100%;
`;

const InviteCodeRow = styled.div`
  border-radius: 4px;
  padding-left: ${({ theme }) => theme.s.xs};
  padding-right: ${({ theme }) => theme.s.xxs};
  background-color: ${({ theme }) => theme.palette.utility.marineBlue[100]};
  display: flex;
  align-items: center;
  white-space: nowrap;
  button {
    :hover {
      color: inherit;
      background-color: inherit;
    }
  }
`;

const InviteCodeText = styled.span`
  padding: ${({ theme }) => theme.s.xxs} 0;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.text.link};
`;

const BrandOptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.s.xs};
`;
